/**
 * The one look-through figure in points: what the years after the round end added to (+) or took from (−) the bank's score, against its reported numbers alone. Every screen that names it
 * (the score story, the solo line, the finale's story, the recognitions) reads this one sign and verb, so none can say "subtracted" for a figure another calls an addition.
 */
import { formatPointsWord } from "../format";

/** Under half a basis point reads "0.00 points": nothing to name. */
export const NO_CHANGE_RATE = 0.00005;

export interface LookThroughMove {
  /** Signed rate: official score minus the score on reported numbers alone. */
  readonly rate: number;
  readonly verb: "added" | "subtracted" | "changed nothing";
  /** "0.84 points" of the move, unsigned (the verb carries the direction). */
  readonly pp: string;
}

export function lookThroughMove(rate: number): LookThroughMove {
  const nothing = Math.abs(rate) < NO_CHANGE_RATE;
  return { rate: nothing ? 0 : rate, verb: nothing ? "changed nothing" : rate < 0 ? "subtracted" : "added", pp: formatPointsWord(Math.abs(rate), { dp: 2 }) };
}

/** From a scorecard: the official rate less the reported-only rate. */
export const lookThroughPp = (s: { readonly valueCreatedRate: number; readonly reportedOnlyValueCreatedRate: number }): LookThroughMove => lookThroughMove(s.valueCreatedRate - s.reportedOnlyValueCreatedRate);

/** From the bank-story give-back (reported-only less official, the opposite sign): the same move. */
export const lookThroughFromGiveBack = (giveBackRate: number): LookThroughMove => lookThroughMove(-giveBackRate);

/** The present tense of the verb, for a sentence about a year still to come ("the look-through years subtract"). */
export const presentVerb = (m: LookThroughMove): string => (m.verb === "added" ? "add" : m.verb === "subtracted" ? "subtract" : "change nothing");
